import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';

const NewsletterVerify = () => {
    
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error' | 'already_verified'
    const [message, setMessage] = useState('');

    useEffect(() => {
        const statusParam = searchParams.get('status');

        if (!statusParam) {
            // No status param yet — backend redirect hasn't landed yet, stay on loading
            return;
        }

        switch (statusParam) {
            case 'success':
                setStatus('success');
                setMessage('Your subscription has been confirmed! Welcome aboard.');
                break;
            case 'already_verified':
                setStatus('already_verified');
                setMessage('Your subscription is already active.');
                break;
            case 'invalid':
            default:
                setStatus('error');
                setMessage('This verification link is invalid or has already been used.');
                break;
        }
    }, [searchParams]);

    const renderContent = () => {
        switch (status) {
            case 'loading':
                return (
                    <div className="flex flex-col items-center">
                        <svg className="w-16 h-16 animate-spin text-green-600 mb-4" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M32 3C35.8083 3 39.5794 3.75011 43.0978 5.20749C46.6163 6.66488 49.8132 8.80101 52.5061 11.4939C55.199 14.1868 57.3351 17.3837 58.7925 20.9022C60.2499 24.4206 61 28.1917 61 32C61 35.8083 60.2499 39.5794 58.7925 43.0978C57.3351 46.6163 55.199 49.8132 52.5061 52.5061C49.8132 55.199 46.6163 57.3351 43.0978 58.7925C39.5794 60.2499 35.8083 61 32 61C28.1917 61 24.4206 60.2499 20.9022 58.7925C17.3837 57.3351 14.1868 55.199 11.4939 52.5061C8.801 49.8132 6.66487 46.6163 5.20749 43.0978C3.7501 39.5794 3 35.8083 3 32C3 28.1917 3.75011 24.4206 5.2075 20.9022C6.66489 17.3837 8.80101 14.1868 11.4939 11.4939C14.1868 8.80099 17.3838 6.66487 20.9022 5.20749C24.4206 3.7501 28.1917 3 32 3L32 3Z" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M32 3C36.5778 3 41.0906 4.08374 45.1692 6.16256C49.2477 8.24138 52.7762 11.2562 55.466 14.9605C58.1558 18.6647 59.9304 22.9531 60.6448 27.4748C61.3591 31.9965 60.9928 36.6232 59.5759 40.9762" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-300" />
                        </svg>
                        <p className="text-gray-600 text-lg font-medium">Verifying your subscription...</p>
                    </div>
                );

            case 'success':
                return (
                    <div className="flex flex-col items-center">
                        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-6">
                            <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">You're subscribed!</h2>
                        <p className="text-gray-600 text-center mb-8">{message}</p>
                        <Link
                            to="/"
                            className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors duration-300"
                        >
                            Back to Home
                        </Link>
                    </div>
                );

            case 'already_verified':
                return (
                    <div className="flex flex-col items-center">
                        <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mb-6">
                            <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Already Verified</h2>
                        <p className="text-gray-600 text-center mb-8">{message}</p>
                        <Link
                            to="/"
                            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors duration-300"
                        >
                            Back to Home
                        </Link>
                    </div>
                );

            case 'error':
            default:
                return (
                    <div className="flex flex-col items-center">
                        <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mb-6">
                            <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Verification Failed</h2>
                        <p className="text-gray-600 text-center mb-8">{message}</p>
                        <Link
                            to="/"
                            className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg transition-colors duration-300"
                        >
                            Back to Home
                        </Link>
                    </div>
                );
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
            <div className="bg-white rounded-2xl shadow-lg p-10 max-w-md w-full text-center">
                <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-800">Newsletter</h1>
                    <p className="text-gray-500 mt-1">Email Verification</p>
                </div>
                {renderContent()}
            </div>
        </div>
    );
};

export default NewsletterVerify;